import { useCallback, useEffect, useState } from "react";
import {
  Sparkles, Loader2, CheckCircle2, XCircle, Layers, GitBranch, Play, Plus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BAND_CLS = {
  CRITICAL: "bg-red-100 text-red-800",
  HIGH: "bg-orange-100 text-orange-800",
  MEDIUM: "bg-amber-100 text-amber-800",
  LOW: "bg-slate-100 text-slate-600",
};
const DIM_CLS = { E: "bg-green-100 text-green-800", S: "bg-sky-100 text-sky-800",
  G: "bg-indigo-100 text-indigo-800" };
const STATUS_LABEL = {
  PROPOSED: "Proposta", UNDER_REVIEW: "In valutazione", ACCEPTED: "Accettata",
  REJECTED: "Scartata", DEFERRED: "Rimandata", IN_PLAN: "Nel piano",
  COMPLETED: "Completata", CANCELLED: "Annullata",
};
const ACTION_STATUS_LABEL = {
  DRAFT: "Bozza", PLANNED: "Pianificata", IN_PROGRESS: "In corso",
  BLOCKED: "Bloccata", COMPLETED: "Completata", VERIFIED: "Verificata", CANCELLED: "Annullata",
};
const DURATION_LABEL = { QUICK_WIN: "0–3 mesi", SHORT_TERM: "3–6 mesi",
  MEDIUM_TERM: "6–12 mesi", LONG_TERM: "oltre 12 mesi" };
const COST_LABEL = { VERY_LOW: "Molto basso", LOW: "Basso", MEDIUM: "Medio",
  HIGH: "Alto", VERY_HIGH: "Molto alto", UNKNOWN: "Non stimato" };
const KANBAN_COLS = ["PLANNED", "IN_PROGRESS", "COMPLETED", "VERIFIED"];
const MATRIX_LABELS = { QUICK_WINS: "Quick wins", STRATEGIC: "Strategiche",
  LOW_PRIORITY: "Bassa priorità", REASSESS: "Da rivalutare" };

export default function PlanV2Sections({ companyId, period, canWrite, canApprove }) {
  const [opps, setOpps] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planDetail, setPlanDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [planName, setPlanName] = useState("");
  const [view, setView] = useState("list");
  const [filterDim, setFilterDim] = useState("");

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    api.get(`/companies/${companyId}/periods/${period.id}/improvement/opportunities`)
      .then((r) => setOpps(r.data.items)).catch(() => setOpps([]));
    api.get(`/companies/${companyId}/periods/${period.id}/improvement/plans`)
      .then((r) => setPlans(r.data.items)).catch(() => setPlans([]));
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const openPlan = async (planId) => {
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/improvement/plans/${planId}`);
      setPlanDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/improvement/opportunities/generate`);
      toast.success(data.created
        ? `Generate ${data.created} opportunità dai gap dello score`
        : "Nessuna nuova opportunità: i gap sono già tutti coperti");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setOppStatus = async (opp, status) => {
    try {
      await api.post(
        `/companies/${companyId}/periods/${period.id}/improvement/opportunities/${opp.id}/status`,
        { status });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createPlan = async () => {
    const accepted = (opps || []).filter((o) => o.status === "ACCEPTED");
    if (!planName.trim() || !accepted.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/improvement/plans`,
        { name: planName.trim(), opportunity_ids: accepted.map((o) => o.id) });
      toast.success(`Piano creato con ${data.actions.length} azioni (bozza)`);
      setPlanName("");
      load();
      openPlan(data.plan.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const planAction = async (planId, action, extra) => {
    try {
      await api.post(
        `/companies/${companyId}/periods/${period.id}/improvement/plans/${planId}/${action}`,
        extra || undefined);
      toast.success(action === "approve" ? "Piano approvato" : "Nuova versione creata (bozza)");
      load();
      openPlan(planId);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const actionStatus = async (action, status) => {
    try {
      if (status === "VERIFIED") {
        await api.post(
          `/companies/${companyId}/periods/${period.id}/improvement/actions/${action.id}/verify`);
        toast.success("Azione verificata. Il dato ESG reale si aggiorna solo dal workflow normale.");
      } else {
        await api.patch(
          `/companies/${companyId}/periods/${period.id}/improvement/actions/${action.id}`,
          { status });
      }
      openPlan(planDetail.plan.id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visibleOpps = (opps || []).filter((o) =>
    !filterDim || o.dimension === filterDim);
  const acceptedCount = (opps || []).filter((o) => o.status === "ACCEPTED").length;
  const actionsById = Object.fromEntries(
    (planDetail?.actions || []).map((a) => [a.id, a]));

  return (
    <div className="space-y-6" data-testid="plan-v2-sections">
      {/* Opportunità dai gap dello score */}
      <Card className="border-slate-200" data-testid="opportunities-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-700" aria-hidden="true" />
              Opportunità di miglioramento (dallo Score ESG PMI)
            </CardTitle>
            <div className="flex items-center gap-2">
              <select value={filterDim} onChange={(e) => setFilterDim(e.target.value)}
                className="text-xs rounded-md border border-slate-300 px-2 py-1.5"
                data-testid="opp-filter-dim" aria-label="Filtra per dimensione">
                <option value="">Tutte le dimensioni</option>
                <option value="E">Environmental</option>
                <option value="S">Social</option>
                <option value="G">Governance</option>
              </select>
              <Button size="sm" disabled={busy} onClick={generate}
                className="bg-green-700 hover:bg-green-800 text-white h-8 text-xs"
                data-testid="generate-opportunities-button">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                  : <Sparkles className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                Analizza gap
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {opps === null ? <p className="text-sm text-slate-500">Caricamento…</p>
            : opps.length === 0 ? (
              <p className="text-sm text-slate-600 py-2" data-testid="opportunities-empty">
                Nessuna opportunità generata. Calcola prima la Valutazione ESG, poi avvia l'analisi dei gap.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="opportunities-list">
                {visibleOpps.map((o) => (
                  <li key={o.id} className="py-3 flex flex-wrap items-start gap-3"
                    data-testid={`opportunity-${o.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={BAND_CLS[o.priority_band] || BAND_CLS.LOW}
                          data-testid={`opp-band-${o.id}`}>
                          {o.priority_band} · {o.priority_score}
                        </Badge>
                        <Badge className={DIM_CLS[o.dimension]}>{o.dimension}</Badge>
                        <span className="text-xs text-slate-500">
                          {STATUS_LABEL[o.status] || o.status}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-900 mt-1">{o.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{o.rationale}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Effort {o.estimated_effort || "—"} · Costo {COST_LABEL[o.estimated_cost_band] || "Non stimato"} · {DURATION_LABEL[o.estimated_duration] || "—"}
                      </p>
                    </div>
                    {canWrite && ["PROPOSED", "UNDER_REVIEW"].includes(o.status) && (
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => setOppStatus(o, "ACCEPTED")}
                          data-testid={`opp-accept-${o.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Accetta
                        </Button>
                        <Button size="sm" variant="outline"
                          className="h-7 text-xs text-red-600 border-red-200"
                          onClick={() => setOppStatus(o, "REJECTED")}
                          data-testid={`opp-reject-${o.id}`}>
                          <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />Scarta
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          {canWrite && acceptedCount > 0 && (
            <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 flex flex-wrap items-center gap-2"
              data-testid="create-plan-box">
              <input type="text" value={planName} onChange={(e) => setPlanName(e.target.value)}
                placeholder={`Nome piano (${acceptedCount} opportunità accettate)`} maxLength={200}
                className="flex-1 min-w-[220px] h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
                data-testid="plan-name-input" aria-label="Nome piano" />
              <Button size="sm" disabled={busy || !planName.trim()} onClick={createPlan}
                className="bg-green-700 hover:bg-green-800 text-white h-9"
                data-testid="create-plan-button">
                <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Crea bozza piano
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Piani */}
      {(plans.length > 0 || planDetail) && (
        <Card className="border-slate-200" data-testid="plans-card">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Layers className="w-4 h-4" aria-hidden="true" /> Piani di miglioramento
              </CardTitle>
              <div className="flex gap-1" data-testid="plan-view-switch">
                {[["list", "Lista"], ["kanban", "Kanban"], ["roadmap", "Roadmap"], ["matrix", "Matrice"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setView(k)}
                    className={`text-xs font-semibold rounded px-2.5 py-1.5 transition-colors ${view === k ? "bg-green-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                    data-testid={`plan-view-${k}`}>{l}</button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {plans.map((p) => (
                <button key={p.id} type="button"
                  onClick={() => openPlan(p.id)}
                  className={`text-xs font-semibold rounded-md border px-3 py-1.5 transition-colors ${planDetail?.plan?.id === p.id ? "border-green-700 bg-green-50 text-green-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`plan-select-${p.id}`}>
                  {p.name} · v{p.version} · {p.status} ({p.open_actions}/{p.actions_count} aperte)
                </button>
              ))}
            </div>

            {planDetail && (
              <div className="space-y-3" data-testid="plan-detail">
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                  <span className="font-semibold text-slate-800 text-sm">{planDetail.plan.name}</span>
                  <Badge className="bg-slate-100 text-slate-700">{planDetail.plan.status}</Badge>
                  {planDetail.base_score_run && (
                    <span data-testid="plan-base-score">
                      Basato su score {planDetail.base_score_run.overall_score ?? "—"}/100 del{" "}
                      {new Date(planDetail.base_score_run.calculated_at).toLocaleDateString("it-IT")}
                      {" "}({planDetail.base_score_run.methodology_version})
                    </span>
                  )}
                  <span>Critiche: {planDetail.summary.critical} · Quick wins: {planDetail.summary.quick_wins} · Verificate: {planDetail.summary.completed}</span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Il completamento del piano non equivale a certificazione di conformità; il progresso non modifica lo score.
                </p>
                {canApprove && ["DRAFT", "UNDER_REVIEW"].includes(planDetail.plan.status) && (
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white h-8 text-xs"
                      onClick={() => planAction(planDetail.plan.id, "approve")}
                      data-testid="approve-plan-button">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Approva piano
                    </Button>
                  </div>
                )}
                {canWrite && planDetail.plan.status === "APPROVED" && (
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => planAction(planDetail.plan.id, "new-version")}
                    data-testid="new-plan-version-button">
                    <GitBranch className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                    Nuova versione sullo score aggiornato
                  </Button>
                )}

                {view === "list" && (
                  <ul className="divide-y divide-slate-100" data-testid="plan-actions-list">
                    {planDetail.actions.map((a) => (
                      <li key={a.id} className="py-2.5 flex flex-wrap items-center gap-3"
                        data-testid={`plan-action-${a.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            {a.priority && <Badge className={BAND_CLS[a.priority]}>{a.priority}</Badge>}
                            <Badge className={DIM_CLS[a.dimension] || DIM_CLS.G}>{a.dimension}</Badge>
                            <span className="text-xs text-slate-500">{ACTION_STATUS_LABEL[a.status]}</span>
                          </div>
                          <p className="text-sm font-medium text-slate-900 mt-0.5">{a.title}</p>
                          <p className="text-xs text-slate-400">
                            Owner {a.owner_role} · Costo {COST_LABEL[a.estimated_cost_band] || "—"} · Verifica: {a.verification_method}
                          </p>
                        </div>
                        {canWrite && (
                          <div className="flex gap-1.5">
                            {a.status === "PLANNED" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => actionStatus(a, "IN_PROGRESS")}
                                data-testid={`action-start-${a.id}`}>
                                <Play className="w-3 h-3 mr-1" aria-hidden="true" />Avvia
                              </Button>
                            )}
                            {a.status === "IN_PROGRESS" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => actionStatus(a, "COMPLETED")}
                                data-testid={`action-complete-${a.id}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Completa
                              </Button>
                            )}
                            {a.status === "COMPLETED" && canApprove && (
                              <Button size="sm" variant="outline"
                                className="h-7 text-xs text-green-800 border-green-300"
                                onClick={() => actionStatus(a, "VERIFIED")}
                                data-testid={`action-verify-${a.id}`}>
                                Verifica
                              </Button>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {view === "kanban" && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="plan-kanban">
                    {KANBAN_COLS.map((col) => (
                      <div key={col} className="rounded-lg bg-slate-50 border border-slate-200 p-2"
                        data-testid={`kanban-col-${col}`}>
                        <p className="text-xs font-bold text-slate-600 mb-2">{ACTION_STATUS_LABEL[col]}</p>
                        <div className="space-y-2">
                          {planDetail.actions.filter((a) => a.status === col).map((a) => (
                            <div key={a.id} className="rounded-md bg-white border border-slate-200 p-2"
                              data-testid={`kanban-card-${a.id}`}>
                              <p className="text-xs font-medium text-slate-800">{a.title}</p>
                              {a.priority && <Badge className={`${BAND_CLS[a.priority]} mt-1`}>{a.priority}</Badge>}
                            </div>
                          ))}
                          {!planDetail.actions.some((a) => a.status === col) && (
                            <p className="text-[11px] text-slate-400">Nessuna azione</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {view === "roadmap" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="plan-roadmap">
                    {Object.entries(DURATION_LABEL).map(([k, label]) => (
                      <div key={k} className="rounded-lg border border-slate-200 p-2.5"
                        data-testid={`roadmap-${k}`}>
                        <p className="text-xs font-bold text-slate-600 mb-2">{label}</p>
                        {(planDetail.views.roadmap[k] || []).map((id) => (
                          <p key={id} className="text-xs text-slate-700 py-0.5">
                            • {actionsById[id]?.title || id}
                          </p>
                        ))}
                        {!(planDetail.views.roadmap[k] || []).length && (
                          <p className="text-[11px] text-slate-400">—</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {view === "matrix" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="plan-matrix">
                    {Object.entries(MATRIX_LABELS).map(([k, label]) => (
                      <div key={k} className="rounded-lg border border-slate-200 p-2.5"
                        data-testid={`matrix-${k}`}>
                        <p className="text-xs font-bold text-slate-600 mb-2">{label}</p>
                        {(planDetail.views.matrix[k] || []).map((id) => (
                          <p key={id} className="text-xs text-slate-700 py-0.5">
                            • {actionsById[id]?.title || id}
                          </p>
                        ))}
                        {!(planDetail.views.matrix[k] || []).length && (
                          <p className="text-[11px] text-slate-400">—</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
